import { z } from 'zod';

/**
 * Official Astral channels surfaced in the app ("Astral Channels"). Managed by
 * users with the `channel:manage` permission (ADMIN/OWNER); visible to everyone
 * with `channel:view`. Deliberately simple so a future "must join @X" gate can
 * be layered on without schema changes.
 */
export interface ChannelDTO {
  id: string;
  /** Display name, if provided. */
  name: string | null;
  /** Telegram username without the leading "@". */
  username: string;
  /** Canonical https://t.me/<username> link. */
  url: string;
  /** Optional short category/label. */
  label: string | null;
  /** Optional short description. */
  description: string | null;
  /** Optional category tag. */
  category: string | null;
  /** Manual sort position (lower = earlier). */
  order: number;
  createdAt: string;
}

/** Build the canonical Telegram link for a username. */
export function channelUrl(username: string): string {
  return `https://t.me/${username}`;
}

const usernameSchema = z
  .string()
  .trim()
  .transform((value) => value.replace(/^@/, ''))
  .pipe(
    z
      .string()
      .regex(/^[A-Za-z0-9_]{4,32}$/, 'Enter a valid Telegram username (4–32 letters, digits or _).'),
  );

export const createChannelSchema = z
  .object({
    username: usernameSchema,
    name: z.string().trim().max(80).optional(),
    label: z.string().trim().max(40).optional(),
    description: z.string().trim().max(160).optional(),
    category: z.string().trim().max(32).optional(),
  })
  .strict();

export type CreateChannelInput = z.infer<typeof createChannelSchema>;

export const updateChannelSchema = z
  .object({
    username: usernameSchema.optional(),
    name: z.string().trim().max(80).nullable().optional(),
    label: z.string().trim().max(40).nullable().optional(),
    description: z.string().trim().max(160).nullable().optional(),
    category: z.string().trim().max(32).nullable().optional(),
    order: z.coerce.number().int().min(0).max(9999).optional(),
  })
  .strict();

export type UpdateChannelInput = z.infer<typeof updateChannelSchema>;
